import { compact, cloneDeep, orderBy, get, map, find } from 'lodash-es';
import { fromJS } from 'immutable';
import uuidv4 from 'uuid/v4';

import offersBlockTransform from './OffersBlock';
import questionnaireBlockTransform from './QuestionnaireBlock';
import loansBlockTransform from './LoansBlock';
import quotationBlockTransform from './QuotationBlock';
import extraDataBlockTransform from './ExtraDataBlock';
import generalDataBlockTransform from './GeneralDataBlock';
import subscriptionBlockTransform from './SubscriptionBlock';
import documentsBlockTransform from './DocumentsBlock';

import { statusSalesRouteEnum } from '../enum';

import { getNameString } from '../utils';

const getActions = ({ id, status }) => {
  switch (status) {
    case statusSalesRouteEnum.CLOSED:
    case statusSalesRouteEnum.SUBSCRIBED:
    case statusSalesRouteEnum.ONHOLD:
      return [
        {
          route: `/salesroutes/${id}`,
          action: 'view',
          type: 'link',
          label: 'salesroute.grid.cell.action.btn.view.label',
        },
      ];

    case statusSalesRouteEnum.COMPUTED:
    case statusSalesRouteEnum.CREATED:
    case statusSalesRouteEnum.PRESUBSCRIBED:
    case statusSalesRouteEnum.PRESIMULATED:
    case statusSalesRouteEnum.LOANSDESCRIBED:
    case statusSalesRouteEnum.COVEREDDESCRIBED:
    case statusSalesRouteEnum.PRINTED:
    case statusSalesRouteEnum.SIMULATED:
    case statusSalesRouteEnum.INPROGRESS:
    default:
      return [
        {
          route: `/salesroutes/${id}`,
          action: 'modify',
          type: 'link',
          label: 'salesroute.grid.cell.action.btn.modify.label',
        },
      ];
  }
};

/**
 * products transform
 * @param {*} products
 */
export const getProductDescriptionsTransform = products => {
  try {
    return map(products, item => item);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
  }
  return [];
};

/**
 *
 * @param {*} questionnaires
 */
export const getQuestionnairesTransform = ({ questionnaires }) => {
  try {
    return questionnaires;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`getQuestionnairesTransform: ${e.stack}`);
  }
  return [];
};

/**
 *
 * @param {*} questionnaires
 */
const getQuestionnaireTransform = ({ questionnaire }) => {
  try {
    return questionnaire;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`getQuestionnaireTransform: ${e.stack}`);
  }
  return {};
};

/**
 *
 * @param {*} salesRoute
 * @param {*} questionnaires
 */
const extendSalesRoute = ({ salesRoute, questionnaires }) => {
  let extendedEntries = {};
  const questionnaireId = get(salesRoute, 'questionnaires[0].questionnaire.id');
  try {
    if (Array.isArray(questionnaires) && questionnaires.length > 0 && questionnaireId) {
      const questionnaire = find(questionnaires, ['id', questionnaireId]);
      extendedEntries = { questionnaireLabel: get(questionnaire, 'title') };
    }

    // Add covereds and subscribers labels
    const parties = get(salesRoute, 'parties', []);

    extendedEntries.coveredsLabel = compact(
      map(get(salesRoute, 'quotations', []), quotation =>
        map(get(quotation, 'covereds', []), ({ party }) => {
          const partyData = find(parties, { ref: party.ref });

          if (partyData) {
            return getNameString(partyData);
          }

          return '';
        })
      ).flat()
    ).join(', ');

    extendedEntries.subscribersLabel = compact(
      map(get(salesRoute, 'contracts', []), contract => {
        const partyData = find(parties, { ref: contract.subscriber.ref });

        if (partyData) {
          return getNameString(partyData);
        }

        return '';
      }).flat()
    ).join(', ');
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
  }
  return extendedEntries;
};

/**
 *
 * @param {*} salesRoute
 */
export const getSalesRouteTransform = ({ salesRoute, questionnaires }) => {
  try {
    const result = {
      id: get(salesRoute, 'id') || get(salesRoute, '_id'),
      contracts: get(salesRoute, 'contracts'),
      createdAt: get(salesRoute, 'createdAt'),
      parties: get(salesRoute, 'parties'),
      questionnaires: get(salesRoute, 'questionnaires'),
      loans: get(salesRoute, 'loans'),
      quotations: get(salesRoute, 'quotations'),
      actions: getActions({ id: get(salesRoute, 'id') || get(salesRoute, '_id'), status: salesRoute?.status }),
      updatedAt: get(salesRoute, 'updatedAt'),
      relations: get(salesRoute, 'relations'),
      status: get(salesRoute, 'status'),
      questionnaire: get(salesRoute, 'questionnaires[0].questionnaire.id'),
      documents: get(salesRoute, 'documents'),
      ...extendSalesRoute({ salesRoute, questionnaires }),
    };
    return result;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`getSalesRouteTransform: ${e.stack}`);
  }
  return {};
};

/**
 *
 * @param {*} salesRoutes
 */
export const getSalesRouteList = salesRoutes => {
  try {
    return salesRoutes.map(salesRoute => getSalesRouteTransform({ salesRoute }));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
  }
  return [];
};
/**
 *
 * @param {*} salesRoutes
 * @param {*} questionnaires
 */
export const retrieveSalesRouteList = ({ salesRoutes = [], questionnaires }) => {
  try {
    return map(salesRoutes, salesRoute => getSalesRouteTransform({ salesRoute, questionnaires }));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
  }
  return [];
};

export const sortedResult = ({ result }) => {
  try {
    return orderBy(result, ['score'], ['desc']);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
  }
  return [];
};

// eslint-disable-next-line no-underscore-dangle
export const transformResult = data => ({ ...data, result: sortedResult(data), id: data._id });

export const getSalesRouteMap = salesRoutes => {
  try {
    return getSalesRouteList(salesRoutes);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
  }
  return [];
};

const getQuestionnaireMap = questionnaires => {
  try {
    const questionnaireList = questionnaires.map(questionnaire => ({
      [get(questionnaire, 'id')]: getQuestionnaireTransform(questionnaire),
    }));
    return fromJS(questionnaireList);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
  }
  return [];
};

export const addQuestionnaireMap = ({ questionnaires1, questionnaires2 }) =>
  getQuestionnaireMap([...questionnaires1, ...questionnaires2]);

/**
 * build Offers Block
 * @param {*} param0
 */
export const buildOffersBlock = ({ salesRoute, questionnaires, products, strings = {} }) =>
  offersBlockTransform({ salesRoute, questionnaires, products, strings });

/**
 * build Questionnaire Block
 * @param {*} param0
 */
export const buildQuestionnaireBlock = ({ salesRoute, questionnaires }) =>
  questionnaireBlockTransform({ salesRoute, questionnaires });

/**
 * build Loans Block
 * @param {*} param0
 */
export const buildLoansBlock = ({ salesRoute, strings = {} }) => loansBlockTransform({ salesRoute, strings });

/**
 * build Questionnaire Block
 * @param {*} param0
 */
export const buildQuotationBlock = ({ salesRoute, relations, strings = {} }) =>
  quotationBlockTransform({ salesRoute, relations, strings });

/**
 * build ExtraData Contract Block
 * @param {*} param0
 */
export const buildExtraDataBlock = ({ salesRoute }) => extraDataBlockTransform({ salesRoute });

/**
 * build GeneralData Block
 * @param {*} param0
 */
export const buildGeneralDataBlock = ({ salesRoute }) => generalDataBlockTransform({ salesRoute });

/**
 * build Subscription Block
 * @param {*} param0
 */
export const buildSubscriptionBlock = ({ salesRoute }) => subscriptionBlockTransform({ salesRoute });

/**
 * build Documents Block
 * @param {*} param0
 */
export const buildDocumentsBlock = ({ salesRoute, strings }) => documentsBlockTransform({ salesRoute, strings });

export const changePersonTypeContract = ({ salesRoute, newPersonType }) => {
  if (newPersonType) {
    salesRoute.contracts = map(salesRoute.contracts, contract => ({
      ...contract,
      subscriber: { ref: contract.covereds[0]?.party.ref },
    }));

    salesRoute.quotations = map(salesRoute.quotations, quotation => ({
      ...quotation,
      subscriber: { ref: quotation.covereds[0]?.party.ref },
    }));
  } else {
    let pm = find(salesRoute.parties, { is_person: newPersonType });

    if (!pm) {
      const genericParty = get(salesRoute, 'questionnaires[0].description.generic_blocks.party', {});
      pm = cloneDeep(genericParty);
      pm.ref = `party_${uuidv4()}`;
      pm.is_person = false;

      salesRoute.parties.push(pm);
    }

    salesRoute.contracts = map(salesRoute.contracts, contract => ({ ...contract, subscriber: { ref: pm.ref } }));
    salesRoute.quotations = map(salesRoute.quotations, quotation => ({ ...quotation, subscriber: { ref: pm.ref } }));
  }

  return salesRoute;
};
